import { housingTopViewHUD } from './HousingTopViewHUD';
/**
 * HousingModeController.ts
 * 
 * Main Housing Mode UI Controller & Input Handler:
 * - Flow:
 *   - Housing ON -> Select Tab -> Select Item -> WASD Grid Cursor & SPACE to Place
 *   - Red Square with White X overlay for invalid placement
 *   - ESC Stack:
 *     - If in Placement -> back to Item Select
 *     - If in Item Select -> back to Tab Select
 *     - If in Tab Select -> Exit Confirmation Modal ("하우징 모드를 종료합니까? 예/아니오")
 * - Tab 1 (Claim Land):
 *   - Real-time drag/tile calculation: 10 Gold per tile (e.g. 200 + 100 = 300 tiles -> 3,000 Gold)
 *   - Admin: 0 Gold, unlimited, dropdown to assign to any user or NPC (King Solaris III, etc.)
 * - Sub-Grid Snap support for Props (16px)
 * - Demolish tool
 */

import {
  HousingTabType,
  HousingCatalogItem,
  LAND_TILE_PRICE_GOLD,
  HOUSING_PRESET_NPCS,
} from '../world/HousingData';
import { housingCatalog } from '../world/HousingCatalog';
import { housingSystem } from '../world/HousingSystem';
import { EntityState } from '../../core/Types';
import { EntityRegistry } from '../../core/EntityRegistry';
import { IsoRenderer } from '../view/IsoRenderer';
import { EventBus } from '../../core/EventBus';
import { Currency } from '../../core/Currency';

export type HousingStage = 'tab_select' | 'item_select' | 'placing' | 'claim_select';

export class HousingModeController {
  private static instance: HousingModeController;

  public isActive = false;
  public currentStage: HousingStage = 'tab_select';
  public selectedTab: HousingTabType = 'claim';
  public selectedItem: HousingCatalogItem | null = null;

  // Grid Cursor state (in World tile coordinates)
  public cursorX = 80;
  public cursorY = 80;
  public rotation = 0; // 0, 90, 180, 270 degrees

  // Draw vs Erase Brush Mode (Toggle with 'R')
  public brushMode: 'draw' | 'erase' = 'draw';
  public isPainting = false;

  // Claim Land Selection state
  public claimSelectedTiles: Map<string, { x: number; y: number }> = new Map();
  public adminSelectedOwnerId: string = 'npc_king_solaris_3';
  public adminSelectedOwnerName: string = '👑 왕, 솔라리스 3세';

  private registry: EntityRegistry | null = null;
  private renderer: IsoRenderer | null = null;
  private getZoneIdCb: (() => number) | null = null;
  private isAdminCb: (() => boolean) | null = null;

  // UI DOM
  private containerEl: HTMLElement | null = null;
  private exitConfirmEl: HTMLElement | null = null;

  // Window Drag & Resize state
  public winX = -1; // -1 means default center-bottom
  public winY = -1;
  public winW = 560; // Compact default width
  public winH = 260; // Compact default height
  public isDraggingWindow = false;
  public isResizingWindow = false;

  public static getInstance(): HousingModeController {
    if (!HousingModeController.instance) {
      HousingModeController.instance = new HousingModeController();
    }
    return HousingModeController.instance;
  }

  public init(
    registry: EntityRegistry,
    renderer: IsoRenderer,
    getZoneId: () => number,
    isAdmin: () => boolean
  ): void {
    this.registry = registry;
    this.renderer = renderer;
    this.getZoneIdCb = getZoneId;
    this.isAdminCb = isAdmin;

    this.buildDOM();
    this.bindGlobalKeys();
  }

  public isHousingActive(): boolean {
    return this.isActive;
  }

  // ==========================================================================
  // 1. ENTER & EXIT HOUSING MODE
  // ==========================================================================

  public enterHousingMode(): void {
    this.isActive = true;
    this.currentStage = 'tab_select';
    this.selectedTab = 'claim';
    this.selectedItem = null;
    this.claimSelectedTiles.clear();

    const player = this.registry?.getPlayer();
    if (player) {
      this.cursorX = Math.round(player.x);
      this.cursorY = Math.round(player.y);
      player.vx = 0;
      player.vy = 0;
    }

    if (this.containerEl) {
      this.containerEl.style.display = 'flex';
    }

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: Date.now(),
      text: '🏠 [하우징 모드 ON] WASD로 커서를 움직이고 SPACE로 설치합니다. (취소: ESC)',
      category: 'system',
    });

    this.render();
  }

  public exitHousingMode(): void {
    this.isActive = false;
    this.selectedItem = null;
    this.claimSelectedTiles.clear();

    if (this.containerEl) {
      this.containerEl.style.display = 'none';
    }
    if (this.exitConfirmEl) {
      this.exitConfirmEl.style.display = 'none';
    }

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: Date.now(),
      text: '🏠 [하우징 모드 OFF] 하우징 모드를 종료하고 캐릭터 모드로 복귀했습니다.',
      category: 'system',
    });
  }

  private promptExitConfirm(): void {
    if (this.exitConfirmEl) {
      this.exitConfirmEl.style.display = 'flex';
    }
  }

  // ==========================================================================
  // 2. INPUT & KEY NAVIGATION (WASD + SPACE + ESC)
  // ==========================================================================

  private bindGlobalKeys(): void {
    window.addEventListener('keydown', (e) => {
      if (!this.isActive) return;

      // Handle ESC Navigation Stack
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.handleEscKey();
        return;
      }

      // Handle Cursor Movement & Actions
      const step = (this.selectedItem?.subGridSnap) ? 0.5 : 1.0;

      if (e.key === 'w' || e.key === 'W' || e.key === 'ArrowUp') {
        this.cursorY -= step;
        e.preventDefault();
      } else if (e.key === 's' || e.key === 'S' || e.key === 'ArrowDown') {
        this.cursorY += step;
        e.preventDefault();
      } else if (e.key === 'a' || e.key === 'A' || e.key === 'ArrowLeft') {
        this.cursorX -= step;
        e.preventDefault();
      } else if (e.key === 'd' || e.key === 'D' || e.key === 'ArrowRight') {
        this.cursorX += step;
        e.preventDefault();
      } else if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        this.handleSpaceAction();
      } else if (e.key === 'Tab') {
        // TAB: Toggle Draw / Erase brush mode
        e.preventDefault();
        this.brushMode = this.brushMode === 'draw' ? 'erase' : 'draw';
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: this.brushMode === 'draw' ? '🖌️ [그리기 모드 (TAB)] 드래그하여 설치/지정합니다.' : '🧹 [지우기 모드 (TAB)] 드래그하여 삭제/선택해제합니다.',
          category: 'system',
        });
        this.render();
      } else if (e.key === 'r' || e.key === 'R') {
        // R: Rotate object 90 degrees
        e.preventDefault();
        this.rotation = (this.rotation + 90) % 360;
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `🔄 [오브젝트 회전 (R)] 현재 각도: ${this.rotation}°`,
          category: 'system',
        });
        this.render();
      } else if (e.key === 'x' || e.key === 'X') {
        // Demolish object at cursor
        this.handleDemolishAction();
      }
    }, true);
  }

  public handleEscKey(): void {
    if (this.exitConfirmEl && this.exitConfirmEl.style.display === 'flex') {
      this.exitConfirmEl.style.display = 'none';
      return;
    }

    if (this.currentStage === 'placing') {
      this.currentStage = 'item_select';
      this.selectedItem = null;
      this.render();
    } else if (this.currentStage === 'item_select' || this.currentStage === 'claim_select') {
      this.currentStage = 'tab_select';
      this.claimSelectedTiles.clear();
      this.render();
    } else if (this.currentStage === 'tab_select') {
      this.promptExitConfirm();
    }
  }

  private handleSpaceAction(): void {
    const zoneId = this.getZoneIdCb ? this.getZoneIdCb() : 1;
    const player = this.registry?.getPlayer();
    const isAdmin = this.isAdminCb ? this.isAdminCb() : false;
    if (!player) return;

    // A. CLAIM LAND MODE
    if (this.selectedTab === 'claim') {
      const tileKey = `${Math.round(this.cursorX)}:${Math.round(this.cursorY)}`;
      if (this.claimSelectedTiles.has(tileKey)) {
        this.claimSelectedTiles.delete(tileKey);
      } else {
        this.claimSelectedTiles.set(tileKey, { x: Math.round(this.cursorX), y: Math.round(this.cursorY) });
      }
      this.render();
      return;
    }

    // B. PLACEMENT MODE
    if (this.selectedItem) {
      // Validate building rights
      const canBuild = housingSystem.canBuildAt(zoneId, this.cursorX, this.cursorY, player, isAdmin);
      if (!canBuild) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: '❌ [설치 불가] 본인 소유의 영지가 아닌 곳에는 건축할 수 없습니다! (소유권 탭에서 땅을 먼저 매입하세요)',
          category: 'combat',
        });
        return;
      }

      const res = housingSystem.placeObject(
        zoneId,
        this.selectedItem.id,
        this.cursorX,
        this.cursorY,
        this.rotation,
        player.id
      );

      if (res.success) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `🔨 ${res.message} (${this.cursorX}, ${this.cursorY})`,
          category: 'system',
        });
      }
    }
  }

  private handleDemolishAction(): void {
    const zoneId = this.getZoneIdCb ? this.getZoneIdCb() : 1;
    const res = housingSystem.demolishObjectAt(zoneId, this.cursorX, this.cursorY);
    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: Date.now(),
      text: res.success ? `🗑️ ${res.message}` : `⚠️ ${res.message}`,
      category: 'system',
    });
  }

  /**
   * Applies painting or erasing at specified world coordinates
   */
  public applyPaintAt(tx: number, ty: number): boolean {
    if (!this.isActive) return false;
    this.cursorX = tx;
    this.cursorY = ty;

    const zoneId = this.getZoneIdCb ? this.getZoneIdCb() : 1;
    const player = this.registry?.getPlayer();
    const isAdmin = this.isAdminCb ? this.isAdminCb() : false;
    if (!player) return false;

    // A. CLAIM LAND TAB
    if (this.selectedTab === 'claim') {
      const tileKey = `${Math.round(tx)}:${Math.round(ty)}`;
      if (this.brushMode === 'erase') {
        if (this.claimSelectedTiles.has(tileKey)) {
          this.claimSelectedTiles.delete(tileKey);
          this.render();
          return true;
        }
      } else {
        if (!this.claimSelectedTiles.has(tileKey)) {
          this.claimSelectedTiles.set(tileKey, { x: Math.round(tx), y: Math.round(ty) });
          this.render();
          return true;
        }
      }
      return true;
    }

    // B. PLACEMENT / ERASE TABS
    if (this.brushMode === 'erase') {
      const res = housingSystem.demolishObjectAt(zoneId, tx, ty);
      return res.success;
    }

    // Draw / Place mode
    if (this.selectedItem) {
      const canBuild = housingSystem.canBuildAt(zoneId, tx, ty, player, isAdmin);
      if (!canBuild) return false;

      housingSystem.placeObject(
        zoneId,
        this.selectedItem.id,
        tx,
        ty,
        this.rotation,
        player.id
      );
      return true;
    }

    return false;
  }

  // ==========================================================================
  // 3. CANVAS OVERLAYS RENDERING (Red square + White X on invalid)
  // ==========================================================================

  public renderCanvasOverlay(ctx: CanvasRenderingContext2D, screenX: number, screenY: number, tileSize: number): void {
    if (!this.isActive) return;

    const zoneId = this.getZoneIdCb ? this.getZoneIdCb() : 1;
    const player = this.registry?.getPlayer();
    const isAdmin = this.isAdminCb ? this.isAdminCb() : false;
    if (!player) return;

    // Draw Claim Selected Tiles in semi-transparent gold
    if (this.selectedTab === 'claim') {
      ctx.fillStyle = this.brushMode === 'erase' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(250, 204, 21, 0.35)';
      ctx.fillRect(screenX - tileSize / 2, screenY - tileSize / 2, tileSize, tileSize);
      ctx.strokeStyle = this.brushMode === 'erase' ? '#ef4444' : '#facc15';
      ctx.lineWidth = 2;
      ctx.strokeRect(screenX - tileSize / 2, screenY - tileSize / 2, tileSize, tileSize);
      return;
    }

    if (this.brushMode === 'erase') {
      ctx.fillStyle = 'rgba(239, 68, 68, 0.4)';
      ctx.fillRect(screenX - tileSize / 2, screenY - tileSize / 2, tileSize, tileSize);
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 2;
      ctx.strokeRect(screenX - tileSize / 2, screenY - tileSize / 2, tileSize, tileSize);
      return;
    }

    const canBuild = housingSystem.canBuildAt(zoneId, this.cursorX, this.cursorY, player, isAdmin);

    if (!canBuild) {
      // ❌ RED SQUARE + WHITE X OVERLAY
      ctx.fillStyle = 'rgba(239, 68, 68, 0.45)';
      ctx.fillRect(screenX - tileSize / 2, screenY - tileSize / 2, tileSize, tileSize);

      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 2.5;
      ctx.strokeRect(screenX - tileSize / 2, screenY - tileSize / 2, tileSize, tileSize);

      // If item selected, also show faint ghost preview even at invalid spot to visualize placement
      if (this.selectedItem) {
        ctx.save();
        ctx.globalAlpha = 0.35;
        this.selectedItem.draw(ctx, screenX, screenY, tileSize, this.rotation);
        ctx.restore();
      }

      // White X
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(screenX - tileSize / 3, screenY - tileSize / 3);
      ctx.lineTo(screenX + tileSize / 3, screenY + tileSize / 3);
      ctx.moveTo(screenX + tileSize / 3, screenY - tileSize / 3);
      ctx.lineTo(screenX - tileSize / 3, screenY + tileSize / 3);
      ctx.stroke();
    } else {
      // ✅ GREEN / CYAN GUIDE PREVIEW
      ctx.fillStyle = 'rgba(56, 189, 248, 0.25)';
      ctx.fillRect(screenX - tileSize / 2, screenY - tileSize / 2, tileSize, tileSize);

      ctx.strokeStyle = '#38bdf8';
      ctx.lineWidth = 2;
      ctx.strokeRect(screenX - tileSize / 2, screenY - tileSize / 2, tileSize, tileSize);

      // If item selected, draw translucent ghost preview with rotation
      if (this.selectedItem) {
        ctx.save();
        ctx.globalAlpha = 0.55; // Semi-transparent ghost preview
        this.selectedItem.draw(ctx, screenX, screenY, tileSize, this.rotation);
        ctx.restore();
      }
    }
  }

  // ==========================================================================
  // 4. DOM UI & TABS
  // ==========================================================================

  private buildDOM(): void {
    // Main Housing Dock Container (Draggable & Resizable & Compact)
    this.containerEl = document.createElement('div');
    this.containerEl.id = 'housing-dock-container';
    this.containerEl.style.cssText = `
      position: fixed;
      bottom: 16px;
      left: 50%;
      transform: translateX(-50%);
      width: 580px;
      min-width: 380px;
      max-width: 96vw;
      min-height: 200px;
      max-height: 80vh;
      background: rgba(15, 23, 42, 0.95);
      border: 2px solid #38bdf8;
      border-radius: 10px;
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.85);
      z-index: 10000;
      color: #f8fafc;
      display: none;
      flex-direction: column;
      font-family: 'Pretendard', sans-serif;
      backdrop-filter: blur(8px);
      user-select: none;
      box-sizing: border-box;
    `;
    document.body.appendChild(this.containerEl);
    this.bindWindowDragAndResize();

    // Exit Confirm Modal
    this.exitConfirmEl = document.createElement('div');
    this.exitConfirmEl.id = 'housing-exit-confirm';
    this.exitConfirmEl.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.75);
      z-index: 10060;
      display: none;
      align-items: center;
      justify-content: center;
      font-family: 'Pretendard', sans-serif;
      backdrop-filter: blur(4px);
    `;
    this.exitConfirmEl.innerHTML = `
      <div style="background: #1e293b; border: 2px solid #38bdf8; border-radius: 12px; padding: 24px; width: 340px; text-align: center; color: #fff; box-shadow: 0 10px 30px rgba(0,0,0,0.8);">
        <div style="font-size: 1.2rem; font-weight: 800; margin-bottom: 8px;">하우징 모드 종료</div>
        <div style="font-size: 0.9rem; color: #cbd5e1; margin-bottom: 20px;">하우징 모드를 종료하고 캐릭터 모드로 복귀하시겠습니까?</div>
        <div style="display: flex; gap: 12px; justify-content: center;">
          <button id="housing-confirm-yes" style="flex: 1; padding: 10px; background: #ef4444; color: #fff; border: none; border-radius: 6px; font-weight: 700; cursor: pointer;">예 (종료)</button>
          <button id="housing-confirm-no" style="flex: 1; padding: 10px; background: #334155; color: #cbd5e1; border: none; border-radius: 6px; font-weight: 700; cursor: pointer;">아니오</button>
        </div>
      </div>
    `;
    document.body.appendChild(this.exitConfirmEl);

    this.exitConfirmEl.querySelector('#housing-confirm-yes')?.addEventListener('click', () => {
      this.exitHousingMode();
    });
    this.exitConfirmEl.querySelector('#housing-confirm-no')?.addEventListener('click', () => {
      if (this.exitConfirmEl) this.exitConfirmEl.style.display = 'none';
    });
  }

  private render(): void {
    if (!this.containerEl) return;

    const isAdmin = this.isAdminCb ? this.isAdminCb() : false;
    const player = this.registry?.getPlayer();
    const playerGold = Currency.breakdown(player?.gold ?? 0).gold;

    this.containerEl.innerHTML = `
      <!-- TOP DRAGGABLE TITLE BAR -->
      <div id="housing-drag-header" style="background: #0f172a; padding: 6px 12px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #1e293b; font-size: 0.76rem; cursor: move;">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="font-weight: 800; color: #38bdf8;">🏰 하우징 모드</span>
          <span style="color: #64748b;">|</span>
          <span style="color: #94a3b8;">커서: (${this.cursorX.toFixed(1)}, ${this.cursorY.toFixed(1)}) | 회전: <b style="color:#38bdf8;">${this.rotation}°</b> (R)</span>
          <button id="housing-btn-toggle-brush-mode" style="background: ${this.brushMode === 'draw' ? '#16a34a' : '#dc2626'}; color: #fff; border: none; padding: 2px 8px; border-radius: 4px; font-weight: 800; font-size: 0.72rem; cursor: pointer;">
            ${this.brushMode === 'draw' ? '🖌️ 그리기 (TAB)' : '🧹 지우기 (TAB)'}
          </button>
          <button id="housing-btn-toggle-topview" style="background: #0284c7; color: #fff; border: none; padding: 2px 8px; border-radius: 4px; font-weight: 800; font-size: 0.72rem; cursor: pointer;" title="직각 평면도 뷰 토글 (V)">
            📐 평면도 뷰 (V)
          </button>
        </div>
        <div style="display: flex; align-items: center; gap: 12px;">
          <span style="color: #facc15; font-weight: 700;">🪙 보유: ${playerGold.toLocaleString()} Gold</span>
          ${isAdmin ? `<span style="background: #dc2626; color: #fff; font-size: 0.68rem; padding: 2px 6px; border-radius: 4px; font-weight: 800;">👑 GM 권한 (무제한)</span>` : ''}
          <button id="housing-btn-exit" style="background: #334155; border: none; color: #f87171; padding: 3px 8px; border-radius: 4px; cursor: pointer; font-size: 0.75rem; font-weight: 700;">종료 (ESC)</button>
        </div>
      </div>

      <!-- TABS BAR -->
      <div style="display: flex; background: #1e293b; border-bottom: 1px solid #334155; overflow-x: auto;">
        ${this.renderTabButton('claim', '1. 소유권표시', '🚩')}
        ${this.renderTabButton('floors', '2. 바닥', '🪵')}
        ${this.renderTabButton('walls', '3. 벽', '🧱')}
        ${this.renderTabButton('furniture', '4. 가구', '🛏️')}
        ${this.renderTabButton('props', '5. 소품', '🍷')}
        ${this.renderTabButton('exterior', '6. 외부인테리어', '🏠')}
        ${this.renderTabButton('facilities', '7. 외부시설', '🌾')}
      </div>

      <!-- CONTENT AREA (Compact & Flexible Scroll) -->
      <div style="padding: 10px 14px; flex: 1; max-height: 220px; overflow-y: auto;">
        ${this.selectedTab === 'claim' ? this.renderClaimTab(isAdmin, playerGold) : this.renderCatalogTab()}
      </div>

      <!-- BOTTOM CONTROL GUIDE & RESIZE HANDLE -->
      <div style="background: #090d16; padding: 6px 12px; display: flex; justify-content: space-between; align-items: center; font-size: 0.72rem; color: #94a3b8; border-top: 1px solid #1e293b; position: relative;">
        <div><b>드래그</b>: 색칠 | <b>TAB</b>: 모드전환 | <b>R</b>: 회전 | <b>ESC</b>: 뒤로</div>
        <div style="display: flex; align-items: center; gap: 8px;">
          <div style="color: #38bdf8; font-size: 0.7rem;"><b>${this.getStageLabel()}</b></div>
          <div id="housing-resize-handle" title="창 크기 조절 (드래그)" style="cursor: nwse-resize; width: 14px; height: 14px; background: linear-gradient(135deg, transparent 50%, #38bdf8 50%); border-bottom-right-radius: 4px;"></div>
        </div>
      </div>
    `;

    this.bindEvents();
  }

  private renderTabButton(tab: HousingTabType, label: string, icon: string): string {
    const isSelected = this.selectedTab === tab;
    return `
      <button data-housing-tab="${tab}" style="
        padding: 8px 14px;
        background: ${isSelected ? '#334155' : 'transparent'};
        color: ${isSelected ? '#38bdf8' : '#94a3b8'};
        border: none;
        border-bottom: 2px solid ${isSelected ? '#38bdf8' : 'transparent'};
        font-weight: 700;
        font-size: 0.8rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 5px;
        white-space: nowrap;
      ">
        <span>${icon}</span> ${label}
      </button>
    `;
  }

  private renderClaimTab(isAdmin: boolean, playerGold: number): string {
    const totalTiles = this.claimSelectedTiles.size;
    const totalGold = isAdmin ? 0 : totalTiles * LAND_TILE_PRICE_GOLD;
    const canAfford = isAdmin || playerGold >= totalGold;

    return `
      <div style="display: flex; flex-direction: column; gap: 10px;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <div style="font-size: 0.85rem; font-weight: 700; color: #facc15;">
            🚩 영지 소유권 구역 지정 (WASD로 이동 후 SPACE로 타일 선택/해제)
          </div>
          <div style="font-size: 0.8rem; color: #94a3b8;">
            타일 1칸당: <b style="color: #facc15;">${LAND_TILE_PRICE_GOLD} Gold</b>
          </div>
        </div>

        ${
          isAdmin
            ? `
          <!-- ADMIN OWNER SELECT DROPDOWN -->
          <div style="background: #1e293b; padding: 8px 12px; border-radius: 6px; display: flex; align-items: center; gap: 10px; border: 1px solid #ef4444;">
            <span style="font-size: 0.8rem; font-weight: 700; color: #f87171;">👑 관리자 소유권 지정 대상:</span>
            <select id="housing-admin-owner-select" style="background: #0f172a; color: #f8fafc; border: 1px solid #475569; padding: 4px 8px; border-radius: 4px; font-size: 0.8rem; flex: 1;">
              ${HOUSING_PRESET_NPCS.map(
                (npc) =>
                  `<option value="${npc.id}" data-name="${npc.name}" ${this.adminSelectedOwnerId === npc.id ? 'selected' : ''}>${npc.name}</option>`
              ).join('')}
              <option value="player_self" data-name="현재 관리자 본인">내 캐릭터 (플레이어)</option>
            </select>
          </div>
        `
            : ''
        }

        <!-- REALTIME ESTIMATE BOX -->
        <div style="background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 12px; display: flex; justify-content: space-between; align-items: center;">
          <div>
            <div style="font-size: 0.8rem; color: #94a3b8;">선택된 타일 수: <b style="color: #38bdf8; font-size: 1rem;">${totalTiles} 칸</b></div>
            <div style="font-size: 0.72rem; color: #64748b;">(예: 집 200칸 + 농지 100칸 = 300칸 ➔ 3,000 Gold)</div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 0.8rem; color: #94a3b8;">총 매입 비용:</div>
            <div style="font-size: 1.15rem; font-weight: 800; color: ${canAfford ? '#facc15' : '#ef4444'};">
              🪙 ${totalGold.toLocaleString()} Gold ${isAdmin ? '(GM 무상)' : ''}
            </div>
          </div>
          <div>
            <button id="housing-btn-claim-confirm" style="
              background: ${canAfford && totalTiles > 0 ? '#16a34a' : '#475569'};
              color: #fff;
              border: none;
              padding: 8px 16px;
              border-radius: 6px;
              font-weight: 700;
              cursor: ${canAfford && totalTiles > 0 ? 'pointer' : 'not-allowed'};
            ">
              소유권 등록 및 결제
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderCatalogTab(): string {
    const items = housingCatalog.getByTab(this.selectedTab);

    return `
      <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px;">
        ${items
          .map((item) => {
            const isSel = this.selectedItem?.id === item.id;
            return `
            <div data-catalog-item="${item.id}" style="
              background: ${isSel ? '#1e3a8a' : '#1e293b'};
              border: 1px solid ${isSel ? '#38bdf8' : '#334155'};
              border-radius: 8px;
              padding: 10px;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 4px;
              cursor: pointer;
              transition: transform 0.1s ease;
            ">
              <div style="font-size: 1.5rem;">${item.icon}</div>
              <div style="font-weight: 700; font-size: 0.75rem; text-align: center; color: #f8fafc;">${item.name}</div>
              <div style="font-size: 0.65rem; color: #94a3b8; text-align: center;">${item.categoryLabel}</div>
            </div>
          `;
          })
          .join('')}
      </div>
    `;
  }

  private getStageLabel(): string {
    if (this.currentStage === 'tab_select') return '탭 선택';
    if (this.currentStage === 'item_select') return '아이템 선택';
    if (this.currentStage === 'placing') return `설치 배치 중 [${this.selectedItem?.name || ''}]`;
    return '소유권 지정';
  }

  private bindWindowDragAndResize(): void {
    if (!this.containerEl) return;
    const el = this.containerEl;

    // Window Dragging via top title bar
    let startX = 0, startY = 0, startL = 0, startT = 0;

    const onPointerMove = (e: PointerEvent) => {
      if (this.isDraggingWindow) {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        const newL = Math.max(0, Math.min(window.innerWidth - el.offsetWidth, startL + dx));
        const newT = Math.max(0, Math.min(window.innerHeight - el.offsetHeight, startT + dy));
        el.style.left = `${newL}px`;
        el.style.top = `${newT}px`;
        el.style.bottom = 'auto';
        el.style.transform = 'none';
        this.winX = newL;
        this.winY = newT;
      } else if (this.isResizingWindow) {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        const newW = Math.max(380, Math.min(window.innerWidth - 20, this.winW + dx));
        const newH = Math.max(200, Math.min(window.innerHeight - 20, this.winH + dy));
        el.style.width = `${newW}px`;
        el.style.height = `${newH}px`;
      }
    };

    const onPointerUp = () => {
      this.isDraggingWindow = false;
      this.isResizingWindow = false;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    el.addEventListener('pointerdown', (e) => {
      const target = e.target as HTMLElement;
      // If clicking resize handle
      if (target.id === 'housing-resize-handle') {
        e.preventDefault();
        e.stopPropagation();
        this.isResizingWindow = true;
        startX = e.clientX;
        startY = e.clientY;
        this.winW = el.offsetWidth;
        this.winH = el.offsetHeight;
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        return;
      }

      // If clicking header drag bar
      const dragHeader = target.closest('#housing-drag-header');
      if (dragHeader && target.tagName !== 'BUTTON' && target.tagName !== 'SELECT') {
        e.preventDefault();
        e.stopPropagation();
        this.isDraggingWindow = true;
        startX = e.clientX;
        startY = e.clientY;
        const rect = el.getBoundingClientRect();
        startL = rect.left;
        startT = rect.top;
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
      }
    });
  }

  private bindEvents(): void {
    if (!this.containerEl) return;

    this.containerEl.querySelector('#housing-btn-exit')?.addEventListener('click', () => {
      this.promptExitConfirm();
    });

    this.containerEl.querySelector('#housing-btn-toggle-brush-mode')?.addEventListener('click', () => {
      this.brushMode = this.brushMode === 'draw' ? 'erase' : 'draw';
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: Date.now(),
        text: this.brushMode === 'draw' ? '🖌️ [그리기 모드] 활성화' : '🧹 [지우기 모드] 활성화',
        category: 'system',
      });
      this.render();
    });

    this.containerEl.querySelector('#housing-btn-toggle-topview')?.addEventListener('click', () => {
      housingTopViewHUD.toggle();
    });

    // Tab switching
    this.containerEl.querySelectorAll('[data-housing-tab]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const tab = (e.currentTarget as HTMLElement).getAttribute('data-housing-tab') as HousingTabType;
        this.selectedTab = tab;
        this.selectedItem = null;
        this.currentStage = tab === 'claim' ? 'claim_select' : 'item_select';
        this.render();
      });
    });

    // Catalog item selection
    this.containerEl.querySelectorAll('[data-catalog-item]').forEach((card) => {
      card.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-catalog-item');
        if (id) {
          const item = housingCatalog.get(id);
          if (item) {
            this.selectedItem = item;
            this.currentStage = 'placing';
            EventBus.emit('LOG_EMITTED', {
              id: Math.random().toString(),
              timestamp: Date.now(),
              text: `🔨 [${item.name}] 선택 완료! WASD로 위치를 잡고 SPACE로 배치하세요. (ESC: 취소)`,
              category: 'system',
            });
            this.render();
          }
        }
      });
    });

    // Admin owner select change
    const ownerSelect = this.containerEl.querySelector('#housing-admin-owner-select') as HTMLSelectElement | null;
    ownerSelect?.addEventListener('change', () => {
      this.adminSelectedOwnerId = ownerSelect.value;
      const opt = ownerSelect.options[ownerSelect.selectedIndex];
      this.adminSelectedOwnerName = opt.getAttribute('data-name') || '지정 소유자';
    });

    // Claim land confirmation button
    this.containerEl.querySelector('#housing-btn-claim-confirm')?.addEventListener('click', () => {
      const zoneId = this.getZoneIdCb ? this.getZoneIdCb() : 1;
      const player = this.registry?.getPlayer();
      const isAdmin = this.isAdminCb ? this.isAdminCb() : false;
      if (!player) return;

      const tiles = Array.from(this.claimSelectedTiles.values());
      const res = housingSystem.claimLandBatch(
        zoneId,
        tiles,
        player,
        isAdmin,
        isAdmin ? this.adminSelectedOwnerId : undefined,
        isAdmin ? this.adminSelectedOwnerName : undefined
      );

      if (res.success) {
        this.claimSelectedTiles.clear();
        this.render();
      } else {
        alert(res.message);
      }
    });
  }
}

export const housingModeController = HousingModeController.getInstance();
